import React, { useState, useEffect, useMemo } from 'react';
import { Button, Select } from 'antd';
import ApplicationService from '../services/axios.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Spinner from './spinner';
import ExcelJS from 'exceljs';
import { FaFileExcel } from 'react-icons/fa';

// Models treated as JV (summed into "JV Total"); everything else rolls into "ODM Total".
const JV_NAMES = ['JV BARCODE', 'JV GSA'];

const ForecastingVsBudgetNew = () => {
  const currentYear = new Date().getFullYear();
  
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(['Q1', 'Q2', 'Q3', 'Q4']);
  const [data, setData] = useState([]);
  const [pivotData, setPivotData] = useState([]);
  const [monthHeaders, setMonthHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const service = new ApplicationService();

  // Month mapping for financial year
  const monthMap = {
    '04': 'Apr', '05': 'May', '06': 'Jun',
    '07': 'Jul', '08': 'Aug', '09': 'Sep',
    '10': 'Oct', '11': 'Nov', '12': 'Dec',
    '01': 'Jan', '02': 'Feb', '03': 'Mar'
  };

  const getMonthsForQuarters = (quarters, year) => {
    const quarterMonths = {
      Q1: ['04', '05', '06'],
      Q2: ['07', '08', '09'],
      Q3: ['10', '11', '12'],
      Q4: ['01', '02', '03']
    };

    let months = [];
    quarters.forEach(q => {
      if (quarterMonths[q]) {
        quarterMonths[q].forEach(month => {
          const actualYear = (q === 'Q4') ? parseInt(year) + 1 : year;
          months.push({
            key: `${actualYear}-${month}`,
            label: monthMap[month],
            month: month,
            year: actualYear
          });
        });
      }
    });
    return months;
  };

  const transformToPivot = (rawData, months) => {
    const customerMap = new Map();

    rawData.forEach(row => {
      if (!customerMap.has(row.customer_id)) {
        customerMap.set(row.customer_id, {
          customer_id: row.customer_id,
          customer_name: row.customer_name,
          months: {}
        });
      }

      const customer = customerMap.get(row.customer_id);
      customer.months[row.month] = {
        budget: parseFloat(row.budget || 0),
        forecast: parseFloat(row.forecast || 0),
        actuals: parseFloat(row.actuals || 0)
      };
    });

    return Array.from(customerMap.values());
  };

  const fetchData = async () => {
    if (selectedQuarter.length === 0) {
      toast.warning('Please select at least one quarter');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        year: selectedYear.toString(),
        quarter: selectedQuarter.join(','),
      };
      
      const response = await service.getBudgetForecastActuals(payload);
      const result = response.data;
      
      if (result.success && result.data) {
        const months = getMonthsForQuarters(selectedQuarter, selectedYear);
        setMonthHeaders(months);
        
        const pivot = transformToPivot(result.data, months);
        setPivotData(pivot);
        setData(result.data);
        
        toast.success('Data fetched successfully!');
      } else {
        setData([]);
        setPivotData([]);
        setMonthHeaders([]);
        toast.info('No data found');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
      setData([]);
      setPivotData([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if a month is in the past, current, or future
  const getMonthStatus = (monthKey) => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYearMonth = `${currentYear}-${currentMonth}`;
    
    if (monthKey < currentYearMonth) return 'past';
    if (monthKey === currentYearMonth) return 'current';
    return 'future';
  };

  const fetchYears = async () => {
    try {
      const response = await service.getBudgetForecastActualsYears();
      if (response.data.statusCode === 200) {
        const years = response.data.data || [];
        const uniqueYears = [...new Set(years.map(year => Number(year)))].sort((a, b) => b - a);
        setAvailableYears(uniqueYears);
        if (uniqueYears.length > 0) {
          setSelectedYear(uniqueYears[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching years:', error);
      setAvailableYears([currentYear, currentYear - 1, currentYear - 2]);
    }
  };

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedQuarter.length > 0) {
      fetchData();
    }
  }, [selectedYear, selectedQuarter]);

  // The two JV models are pulled out of the main list, summed into "JV Total",
  // while the remaining models roll up into "ODM Total". Layout requested:
  //   ...ODM models, ODM Total, JV BARCODE, JV GSA, JV Total, Grand Total
  const displayRows = useMemo(() => {
    if (!pivotData.length) return [];

    const isJv = (r) => JV_NAMES.includes((r.customer_name || '').trim().toUpperCase());

    // Sum a set of model rows into one synthetic total row.
    const sumRows = (rows, name) => {
      const months = {};
      monthHeaders.forEach((m) => {
        months[m.key] = { budget: 0, actuals: 0, forecast: 0 };
      });
      rows.forEach((r) => {
        monthHeaders.forEach((m) => {
          const md = r.months?.[m.key] || { budget: 0, actuals: 0, forecast: 0 };
          months[m.key].budget += Number(md.budget) || 0;
          months[m.key].actuals += Number(md.actuals) || 0;
          months[m.key].forecast += Number(md.forecast) || 0;
        });
      });
      return { customer_id: `__${name}`, customer_name: name, months, isTotal: true };
    };

    const others = pivotData.filter((r) => !isJv(r));
    // Keep JV rows in the requested order (BARCODE then GSA), ignoring source order.
    const jvRows = JV_NAMES
      .map((n) => pivotData.find((r) => (r.customer_name || '').trim().toUpperCase() === n))
      .filter(Boolean);

    return [
      ...others,
      sumRows(others, 'ODM Total'),
      ...jvRows,
      sumRows(jvRows, 'JV Total'),
      sumRows(pivotData, 'Grand Total'),
    ];
  }, [pivotData, monthHeaders]);

  // Which quarter a financial month belongs to.
  const quarterOf = (monthNum) => {
    if (['04', '05', '06'].includes(monthNum)) return 'Q1';
    if (['07', '08', '09'].includes(monthNum)) return 'Q2';
    if (['10', '11', '12'].includes(monthNum)) return 'Q3';
    return 'Q4';
  };

  // Group the visible month columns by quarter, so each quarter can get its own
  // Budget/Actual total columns at the end of the table.
  const quarterGroups = useMemo(() => {
    const groups = [];
    const byQuarter = {};
    monthHeaders.forEach((m) => {
      const q = quarterOf(m.month);
      if (!byQuarter[q]) {
        byQuarter[q] = { quarter: q, months: [] };
        groups.push(byQuarter[q]);
      }
      byQuarter[q].months.push(m);
    });
    return groups;
  }, [monthHeaders]);

  // Sum a row's budget and actuals across the months of one quarter.
  const sumQuarter = (customer, months) => {
    let budget = 0;
    let actuals = 0;
    months.forEach((m) => {
      const md = customer.months?.[m.key] || {};
      budget += Number(md.budget) || 0;
      actuals += Number(md.actuals) || 0;
    });
    return { budget, actuals };
  };

  const handleSearch = () => {
    fetchData();
  };

  const handleExport = async () => {
    try {
      if (!pivotData || pivotData.length === 0) {
        toast.error('No data to export');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Budget Forecast Actuals');

      // Fixed columns
      const fixedColumns = ['Customer Name'];
      
      // Create first header row (Month names merged). Columns are laid out
      // quarter by quarter: each quarter's months, then that quarter's totals.
      const headerRow1 = [...fixedColumns];
      quarterGroups.forEach(g => {
        g.months.forEach(month => {
          const status = getMonthStatus(month.key);
          let colCount = 1; // Budget always shown
          if (status === 'past' || status === 'current') colCount++; // + Actual
          if (status === 'current' || status === 'future') colCount++; // + FC

          headerRow1.push(month.label);
          for (let i = 1; i < colCount; i++) {
            headerRow1.push('');
          }
        });
        headerRow1.push(`${g.quarter} Total`);
        headerRow1.push('');
      });
      worksheet.addRow(headerRow1);

      // Create second header row (Column names)
      const headerRow2 = [...fixedColumns];
      quarterGroups.forEach(g => {
        g.months.forEach(month => {
          const status = getMonthStatus(month.key);
          headerRow2.push('Budget');
          if (status === 'past' || status === 'current') headerRow2.push('Actual');
          if (status === 'current' || status === 'future') headerRow2.push('FC');
        });
        headerRow2.push('Budget Total');
        headerRow2.push('Actual Total');
      });
      worksheet.addRow(headerRow2);

      // Merge cells for month + quarter-total headers, in the same order
      let currentCol = fixedColumns.length + 1;
      quarterGroups.forEach(g => {
        g.months.forEach(month => {
          const status = getMonthStatus(month.key);
          let colCount = 1;
          if (status === 'past' || status === 'current') colCount++;
          if (status === 'current' || status === 'future') colCount++;

          const startCol = currentCol;
          const endCol = currentCol + colCount - 1;
          if (startCol !== endCol) {
            worksheet.mergeCells(1, startCol, 1, endCol);
          }
          currentCol = endCol + 1;
        });
        // Merge the two cells under this quarter's total group header
        worksheet.mergeCells(1, currentCol, 1, currentCol + 1);
        currentCol += 2;
      });

      // Style header rows
      worksheet.getRow(1).font = { bold: true, size: 11 };
      worksheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFEB3B' }
      };
      worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 25;

      worksheet.getRow(2).font = { bold: true, size: 10 };
      worksheet.getRow(2).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' }
      };
      worksheet.getRow(2).alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(2).height = 20;

      // Add data rows (including the ODM Total / JV Total / Grand Total rows)
      displayRows.forEach(customer => {
        const row = [customer.customer_name];

        quarterGroups.forEach(g => {
          g.months.forEach(month => {
            const status = getMonthStatus(month.key);
            const monthData = customer.months[month.key] || { budget: 0, forecast: 0, actuals: 0 };

            row.push(Number(monthData.budget) || 0);
            if (status === 'past' || status === 'current') row.push(Number(monthData.actuals) || 0);
            if (status === 'current' || status === 'future') row.push(Number(monthData.forecast) || 0);
          });

          // This quarter's budget/actual totals, right after its months
          const { budget, actuals } = sumQuarter(customer, g.months);
          row.push(Number(budget) || 0);
          row.push(Number(actuals) || 0);
        });

        const excelRow = worksheet.addRow(row);
        if (customer.isTotal) {
          excelRow.font = { bold: true, size: 10 };
        }
      });

      // Auto-fit columns
      worksheet.columns.forEach((column, index) => {
        if (index === 0) {
          column.width = 25;
        } else {
          column.width = 12;
          column.numFmt = '#,##0.00';
        }
      });

      // Add borders to all cells
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } }
          };
          
          // Align data cells
          if (rowNumber > 2) {
            cell.alignment = { horizontal: 'right', vertical: 'middle' };
          }
        });
        
        // Left align customer name
        if (rowNumber > 2) {
          row.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
        }
      });

      // Generate file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_forecast_actuals_${selectedYear}_Q${selectedQuarter.join('_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel file');
    }
  };

  return (
    <div className="p-4 bg-[#f9fafb]">
      <h2 className="text-2xl font-bold mb-4">Budget, Forecast & Actuals</h2>

      {/* Filter Section */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-4">
        <div className="flex items-center gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Year</label>
            <Select
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ width: 120 }}
              options={availableYears.map(year => ({ label: year, value: year }))}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Quarter</label>
            <Select
              mode="multiple"
              value={selectedQuarter}
              onChange={setSelectedQuarter}
              style={{ width: 240 }}
              placeholder="Select quarters"
              options={[
                { label: 'Q1', value: 'Q1' },
                { label: 'Q2', value: 'Q2' },
                { label: 'Q3', value: 'Q3' },
                { label: 'Q4', value: 'Q4' }
              ]}
            />
          </div>

          <div style={{ marginTop: '24px' }}>
            <button
              onClick={handleSearch}
              className="bg-[#3D3D3D] text-white px-6 py-2 rounded-md cursor-pointer hover:bg-[#2D2D2D]"
              style={{ height: '40px' }}
            >
              <span className="text-white">Search</span>
            </button>
          </div>

          {pivotData.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <button
                onClick={handleExport}
                className="bg-green-600 text-white px-4 py-2 rounded-md flex items-center gap-2 hover:bg-green-700"
                style={{ height: '40px' }}
              >
                <FaFileExcel />
                Export Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="bg-white p-4 rounded-lg shadow-md">
          {pivotData.length > 0 ? (
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="min-w-full border-collapse border border-gray-300">
                <thead className="sticky top-0 z-20">
                  <tr className="bg-[#EAEEFF]">
                    <th className="border border-gray-300 px-4 py-2 text-left sticky left-0 bg-[#EAEEFF] z-30" style={{ minWidth: '150px' }}>
                      Models
                    </th>
                    {quarterGroups.map((g, gi) => (
                      <React.Fragment key={`qh-${gi}`}>
                        {g.months.map((month, idx) => {
                          const status = getMonthStatus(month.key);
                          return (
                            <React.Fragment key={idx}>
                              <th className="border border-gray-300 px-2 py-2 text-center bg-[#E8E8FF]" style={{ minWidth: '90px', fontSize: '11px' }}>
                                {month.label} Budget
                              </th>
                              {(status === 'past' || status === 'current') && (
                                <th className="border border-gray-300 px-2 py-2 text-center bg-[#D4A5D4]" style={{ minWidth: '90px', fontSize: '11px' }}>
                                  {month.label} ACTUAL
                                </th>
                              )}
                              {(status === 'current' || status === 'future') && (
                                <th className="border border-gray-300 px-2 py-2 text-center bg-[#FFE8CC]" style={{ minWidth: '90px', fontSize: '11px' }}>
                                  {month.label} FC
                                </th>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {/* This quarter's total columns, right after its months */}
                        <th className="border border-gray-300 px-2 py-2 text-center bg-[#B9CDF2]" style={{ minWidth: '100px', fontSize: '11px' }}>
                          {g.quarter} Budget Total
                        </th>
                        <th className="border border-gray-300 px-2 py-2 text-center bg-[#C79BC7]" style={{ minWidth: '100px', fontSize: '11px' }}>
                          {g.quarter} ACTUAL Total
                        </th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayRows.map((customer, idx) => {
                    const isTotal = customer.isTotal;
                    // Each total row gets its own light tint so they read apart at a glance.
                    const totalBgByName = {
                      'ODM Total': 'bg-[#E6F4EA]',   // light green
                      'JV Total': 'bg-[#FEF3E2]',    // light amber
                      'Grand Total': 'bg-[#DCE3F7]', // light blue (strongest)
                    };
                    const rowBg = isTotal ? (totalBgByName[customer.customer_name] || 'bg-[#EEF2FB]') : 'bg-white';
                    const rowClass = isTotal ? `${rowBg} font-bold` : 'hover:bg-gray-50';
                    const stickyBg = rowBg;

                    return (
                      <tr key={idx} className={rowClass}>
                        <td
                          className={`border border-gray-300 px-4 py-2 font-medium sticky left-0 z-10 ${stickyBg} ${isTotal ? 'font-bold' : ''}`}
                          style={{ minWidth: '150px' }}
                        >
                          {customer.customer_name}
                        </td>
                        {quarterGroups.map((g, gi) => {
                          const { budget: qBudget, actuals: qActuals } = sumQuarter(customer, g.months);
                          return (
                            <React.Fragment key={`qb-${gi}`}>
                              {g.months.map((month, mIdx) => {
                                const monthData = customer.months[month.key] || { budget: 0, actuals: 0, forecast: 0 };
                                const status = getMonthStatus(month.key);

                                return (
                                  <React.Fragment key={mIdx}>
                                    <td className="border border-gray-300 px-2 py-2 text-center" style={{ minWidth: '90px', fontSize: '11px' }}>
                                      {monthData.budget.toFixed(2)}
                                    </td>
                                    {(status === 'past' || status === 'current') && (
                                      <td className={`border border-gray-300 px-2 py-2 text-center ${isTotal ? '' : 'bg-[#F9F0F9]'}`} style={{ minWidth: '90px', fontSize: '11px' }}>
                                        {monthData.actuals.toFixed(2)}
                                      </td>
                                    )}
                                    {(status === 'current' || status === 'future') && (
                                      <td className={`border border-gray-300 px-2 py-2 text-center ${isTotal ? '' : 'bg-[#FFF8F0]'}`} style={{ minWidth: '90px', fontSize: '11px' }}>
                                        {monthData.forecast.toFixed(2)}
                                      </td>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                              {/* This quarter's budget/actual totals, right after its months */}
                              <td className={`border border-gray-300 px-2 py-2 text-center font-semibold ${isTotal ? '' : 'bg-[#E3ECFB]'}`} style={{ minWidth: '100px', fontSize: '11px' }}>
                                {qBudget.toFixed(2)}
                              </td>
                              <td className={`border border-gray-300 px-2 py-2 text-center font-semibold ${isTotal ? '' : 'bg-[#F1E4F1]'}`} style={{ minWidth: '100px', fontSize: '11px' }}>
                                {qActuals.toFixed(2)}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No data available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ForecastingVsBudgetNew;

